import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useApi } from '../../lib/useApi.js';
import {
  ErrorState,
  HealthIndicator,
  LoadingSkeleton,
  PageViewerControls,
  StatusChip,
} from '../../components/index.js';
import { humanizeEnum } from '../../lib/fieldLabels.js';
import {
  ActivityHistoryView,
  DecisionsBlockersView,
  HandoffPreviewView,
  OperatingPictureView,
  OverviewSectionNav,
  OverviewSummaryPanel,
} from './OverviewSections.jsx';
import {
  ExtractionReviewView,
  IntakeSectionNav,
  IntakeSummaryPanel,
  IntakeSummaryView,
  OperatorExtractionView,
  RfisStakeholdersView,
  SourceTranscriptView,
} from './IntakeSections.jsx';
import { RapidActionsBoard, RapidPhaseRail, RapidPoamTable } from './RapidSharedChrome.jsx';
import {
  buildDerivedActions,
  buildDerivedActivity,
  buildDerivedBarriers,
  buildDerivedRfIs,
  buildPhaseRail,
  calculateRapidCriteria,
  clone,
  formatCriteriaRecommendation,
  formatLongDate,
  formatMoney,
  summarizePodHealth,
} from './rapidPageUtils.js';

export function OverviewTab({ detail, detailState }) {
  const rapid = detail.rapid;
  const [activeSection, setActiveSection] = useState('summary');
  const shows = (key) => activeSection === 'all' || activeSection === key;

  const barriers = buildDerivedBarriers(rapid, detailState);
  const openBlockers = barriers.filter(
    (b) => !/resolved|closed|done|complete/i.test(String(b.status || '')),
  ).length;

  const phaseLane = buildPhaseRail(rapid).find((lane) => lane.state === 'active');
  const phaseLabel = phaseLane?.label || rapid.phase || '—';

  const nextMove = openBlockers
    ? `Resolve ${openBlockers} open ${openBlockers === 1 ? 'item' : 'items'}: ${
        barriers.find((b) => !/resolved|closed|done|complete/i.test(String(b.status || '')))
          ?.decisionNeeded || 'sponsor decision pending.'
      }`
    : `Advance to closeout — ${rapid.closeout?.closeoutReadiness?.percent}% ready; confirm sustainment owners.`;

  const activityEntries = buildDerivedActivity(detail, detailState).map((entry, index) => ({
    key: `${entry.ts || entry.createdAt || 'local'}-${index}`,
    date: entry.ts || formatLongDate(entry.createdAt),
    actor: entry.actor || entry.createdBy || 'Local operator',
    text: entry.event || entry.text,
  }));

  const handoffPreview = {
    label: rapid.projectPreview?.label,
    narrative:
      detail.projectPreviewRecord?.originNarrative || detail.projectPreviewRecord?.description,
    projectId: rapid.projectPreview?.projectId,
    status: detail.projectPreviewRecord?.status || rapid.projectPreview?.projectStatus,
  };

  return (
    <div className="rapid-tab-panel overview-tab-panel" data-testid="rapid-overview-tab">
      <OverviewSummaryPanel
        phaseLabel={phaseLabel}
        health={rapid.healthOverall}
        score={rapid.criteriaScore.total.toFixed(1)}
        recommendation={formatCriteriaRecommendation(rapid.criteriaScore)}
        daysRemaining={rapid.timeline?.daysRemaining}
        weekLabel={`Week ${rapid.timeline?.currentWeek} of ${rapid.timeline?.totalWeeks}`}
        closeoutPercent={rapid.closeout?.closeoutReadiness?.percent}
        closeoutComplete={rapid.closeout?.closeoutReadiness?.complete}
        closeoutTotal={rapid.closeout?.closeoutReadiness?.total}
        valueDisplay={rapid.valueSummary?.displayValue || '—'}
        costAvoidanceLabel={formatMoney(rapid.valueSummary?.costAvoidanceUsd)}
        openBlockers={openBlockers}
        nextMove={nextMove}
      />

      <RapidPhaseRail rapid={rapid} />

      <OverviewSectionNav active={activeSection} onSelect={setActiveSection} />

      <div className="overview-views">
        <div className="overview-view" hidden={!shows('summary')}>
          <section className="rapid-panel" data-testid="overview-summary-view">
            <div className="rapid-section-heading">
              <div>
                <div className="eyebrow">Start here</div>
                <h3>What this snapshot covers</h3>
              </div>
            </div>
            <p>
              The command snapshot and phase rail above stay pinned. Use the views below to drill
              into the operating picture, the open decisions and blockers a sponsor must clear, the
              full change history, and the handoff path to the sustainment project.
            </p>
            <ul className="rapid-inline-list">
              <li>
                <strong>Operating Picture</strong> — problem, North Star, sponsors, and approval
                state.
              </li>
              <li>
                <strong>Decisions &amp; Blockers</strong> — what needs a sponsor decision before
                closeout.
              </li>
              <li>
                <strong>Activity History</strong> — the recorded and locally-generated change log.
              </li>
              <li>
                <strong>Handoff Preview</strong> — the PRIME-derived sustainment project.
              </li>
            </ul>
          </section>
        </div>

        <div className="overview-view" hidden={!shows('operating')}>
          <OperatingPictureView
            rapid={rapid}
            approvedLabel={formatLongDate(rapid.approval?.approvedDate)}
          />
        </div>

        <div className="overview-view" hidden={!shows('decisions')}>
          <DecisionsBlockersView barriers={barriers} />
        </div>

        <div className="overview-view" hidden={!shows('activity')}>
          <ActivityHistoryView entries={activityEntries} />
        </div>

        <div className="overview-view" hidden={!shows('handoff')}>
          <HandoffPreviewView preview={handoffPreview} />
        </div>
      </div>
    </div>
  );
}

export function IntakeTab({ rapid, detailState, onStateChange }) {
  const [activeSection, setActiveSection] = useState('summary');
  const shows = (key) => activeSection === 'all' || activeSection === key;

  const intake = detailState.intake;
  const fields = intake.fields || {};
  const rfis = buildDerivedRfIs(rapid, detailState);
  const fieldReview = intake.fieldReview || {};

  const reviewCounts = ['title', 'problemStatement', 'northStar'].reduce(
    (acc, field) => {
      const status = fieldReview[field] || 'Accepted';
      if (status === 'Needs edit') acc.needsEdit += 1;
      else if (status === 'Rejected') acc.rejected += 1;
      else acc.accepted += 1;
      return acc;
    },
    { accepted: 0, needsEdit: 0, rejected: 0 },
  );

  function patchIntake(patch) {
    onStateChange({ ...detailState, intake: { ...intake, ...patch } });
  }

  function updateFields(next) {
    patchIntake({ fields: { ...fields, ...next } });
  }

  function updateFieldReview(field, status) {
    patchIntake({ fieldReview: { ...fieldReview, [field]: status } });
  }

  function setMode(mode) {
    patchIntake(mode === 'seeded' ? { mode: 'seeded', liveFailure: false } : { mode: 'operator' });
  }

  function runExtraction() {
    patchIntake({
      liveFailure: false,
      extractionStatus: 'Accepted',
      fields: clone(rapid.expectedExtraction || {}),
    });
  }

  function testFallback() {
    patchIntake({
      liveFailure: true,
      extractionStatus: 'Accepted',
      fields: clone(rapid.expectedExtraction || {}),
    });
  }

  function runCustom() {
    patchIntake({
      fields: clone(rapid.expectedExtraction || {}),
      customEcho: intake.customText,
      extractionStatus: 'Accepted',
    });
  }

  return (
    <div className="rapid-tab-panel intake-tab-panel" data-testid="rapid-intake-tab">
      <IntakeSummaryPanel
        mode={intake.mode}
        acknowledged={intake.acknowledgedExperimental}
        extractionStatus={intake.extractionStatus}
        reviewCounts={reviewCounts}
        rfiCount={rfis.length}
        stakeholderCount={Object.keys(fields.stakeholders || {}).length}
        onSetMode={setMode}
      />

      <IntakeSectionNav active={activeSection} onSelect={setActiveSection} />

      <div className="intake-views">
        <div className="intake-view" hidden={!shows('summary')}>
          <IntakeSummaryView />
        </div>
        <div className="intake-view" hidden={!shows('operator')}>
          <OperatorExtractionView
            mode={intake.mode}
            acknowledged={intake.acknowledgedExperimental}
            liveFailure={intake.liveFailure}
            customText={intake.customText}
            customEcho={intake.customEcho}
            onToggleAck={(checked) => patchIntake({ acknowledgedExperimental: checked })}
            onRunExtraction={runExtraction}
            onTestFallback={testFallback}
            onCustomTextChange={(value) => patchIntake({ customText: value })}
            onRunCustom={runCustom}
          />
        </div>
        <div className="intake-view" hidden={!shows('review')}>
          <ExtractionReviewView
            fields={fields}
            fieldReview={fieldReview}
            onUpdateField={updateFields}
            onSetFieldReview={updateFieldReview}
            onSetExtractionStatus={(status) => patchIntake({ extractionStatus: status })}
          />
        </div>
        <div className="intake-view" hidden={!shows('rfis')}>
          <RfisStakeholdersView fields={fields} rfis={rfis} />
        </div>
        <div className="intake-view" hidden={!shows('transcript')}>
          <SourceTranscriptView transcript={rapid.sourceTranscript} />
        </div>
      </div>
    </div>
  );
}

export function CriteriaTab({ rapid }) {
  const [draft, setDraft] = useState(calculateRapidCriteria(rapid.criteriaScore.rows || []));

  useEffect(() => {
    setDraft(calculateRapidCriteria(rapid.criteriaScore.rows || []));
  }, [rapid.criteriaScore.rows]);

  function updateRow(rowKey, nextScore) {
    setDraft((current) =>
      calculateRapidCriteria(
        current.rows.map((row) =>
          row.key === rowKey ? { ...row, selectedScore: Number(nextScore) } : row,
        ),
      ),
    );
  }

  return (
    <div className="rapid-tab-panel" data-testid="rapid-criteria-tab">
      <section className="rapid-panel">
        <div className="rapid-section-heading">
          <div>
            <div className="eyebrow">Criteria</div>
            <h3>Weighted criteria matrix</h3>
          </div>
          <StatusChip status={draft.total >= draft.threshold ? 'approved' : 'routed'}>
            {draft.total.toFixed(1)} / {draft.threshold}
          </StatusChip>
        </div>
        <p>{draft.recommendation}</p>
        <div className="rapid-criteria-list">
          {draft.rows.map((row) => (
            <div
              className="rapid-criteria-row"
              data-testid={`criteria-row-${row.key}`}
              key={row.key}
            >
              <div className="row spread rapid-criteria-row-head">
                <div>
                  <strong>{row.criterion}</strong>
                  <div className="muted small">{row.description}</div>
                </div>
                <span className="chip outline sm">Weight {row.weight}</span>
              </div>
              <div className="rapid-inline-actions rapid-criteria-scale">
                {[1, 5, 9].map((score) => (
                  <button
                    key={score}
                    type="button"
                    className={`btn ${row.selectedScore === score ? 'primary' : 'ghost'} sm`}
                    onClick={() => updateRow(row.key, score)}
                  >
                    {score}
                  </button>
                ))}
              </div>
              <dl className="rapid-criteria-meta">
                <div>
                  <dt>Weighted</dt>
                  <dd>
                    {Number(row.weightedContribution || 0).toFixed(1)}
                    <span className="muted"> · {row.rationale}</span>
                  </dd>
                </div>
                <div>
                  <dt>Evidence note</dt>
                  <dd>{row.evidenceNote || 'Pending evidence note'}</dd>
                </div>
                <div>
                  <dt>AI suggestion</dt>
                  <dd>
                    {row.aiSuggestedScore ?? row.selectedScore} · Human confirmed:{' '}
                    {row.humanConfirmed ? 'Yes' : 'No'}
                  </dd>
                </div>
              </dl>
            </div>
          ))}
        </div>
        <div className="rapid-inline-actions">
          <button
            type="button"
            className="btn ghost sm"
            onClick={() => setDraft(calculateRapidCriteria(rapid.criteriaScore.rows || []))}
          >
            Restore criteria score
          </button>
        </div>
      </section>
      {rapid.approval ? (
        <section className="rapid-panel">
          <div className="eyebrow">Approval / delegation</div>
          <h3 title={rapid.approval.approvalState}>{humanizeEnum(rapid.approval.approvalState)}</h3>
          <p>{humanizeEnum(rapid.approval.delegationMemo?.artifactType)}</p>
          <p>{humanizeEnum(rapid.approval.delegationMemo?.memoStatus)}</p>
        </section>
      ) : null}
      {rapid.routedDecisionId ? (
        <section className="rapid-panel">
          <div className="eyebrow">No-go proof</div>
          <h3 title={rapid.disposition}>{humanizeEnum(rapid.disposition)}</h3>
          <p>{rapid.routedNote}</p>
          <span className="chip outline sm">{rapid.routedDecisionId}</span>
        </section>
      ) : null}
    </div>
  );
}

export function ApprovalTab({ detail, detailState }) {
  const approval = detail.rapid.approval;
  const delegationArtifact = (detail.artifacts || []).find(
    (artifact) => artifact.type === 'delegationMemo',
  );
  const approvalActivity = buildDerivedActivity(detail, detailState).filter((entry) =>
    String(entry.event || entry.text || '')
      .toLowerCase()
      .match(/approv|delegation/),
  );

  return (
    <div className="rapid-tab-panel" data-testid="rapid-approval-tab">
      <div className="rapid-grid two-up">
        <section className="rapid-panel">
          <div className="eyebrow">Approval & delegation</div>
          <h3>Delegation memo</h3>
          <div className="rapid-chip-row">
            <StatusChip status={approval?.approvalState}>{approval?.approvalState}</StatusChip>
            <span className="chip outline sm">{approval?.delegationMemo?.artifactType}</span>
          </div>
          <p>{approval?.delegationMemo?.memoStatus}</p>
          <ul className="rapid-inline-list">
            <li>
              <strong>Approver:</strong> {approval?.approver}
            </li>
            <li>
              <strong>Approved:</strong> {formatLongDate(approval?.approvedDate)}
            </li>
            <li>
              <strong>Defined scope:</strong> {approval?.delegationMemo?.definedScope}
            </li>
            <li>
              <strong>Defined duration:</strong> {approval?.delegationMemo?.definedDuration}
            </li>
          </ul>
          {/* External UAT FINDING-014: the scope boundary is stated, not implied. */}
          <div className="rapid-note" data-testid="delegation-scope-boundary">
            Work outside this defined scope requires separate approval before execution.
          </div>
        </section>
        <section className="rapid-panel">
          <div className="eyebrow">Artifact framing</div>
          <h3>{delegationArtifact?.title || 'Delegation memo artifact'}</h3>
          <p>{delegationArtifact?.summary || approval?.delegationMemo?.memoStatus}</p>
          <div className="rapid-note">
            Approval decisions remain visible before pod execution so sponsor direction is not lost
            in later tabs.
          </div>
        </section>
      </div>
      <section className="rapid-panel">
        <div className="eyebrow">Approval activity</div>
        <ul className="rapid-record-list">
          {approvalActivity.map((entry, index) => (
            <li key={`${entry.ts || entry.createdAt || 'approval'}-${index}`}>
              <div className="muted small">{entry.ts || formatLongDate(entry.createdAt)}</div>
              <strong>{entry.actor || entry.createdBy || 'Local operator'}</strong>
              <p>{entry.event || entry.text}</p>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}

export function PodsTab({ detail }) {
  const [selectedPodId, setSelectedPodId] = useState(detail.pods?.[0]?.id || 'POD-1');
  const [selectedDriverId, setSelectedDriverId] = useState(
    detail.pods?.[0]?.driverNodes?.[0]?.id || null,
  );

  useEffect(() => {
    setSelectedPodId(detail.pods?.[0]?.id || 'POD-1');
    setSelectedDriverId(detail.pods?.[0]?.driverNodes?.[0]?.id || null);
  }, [detail.pods]);

  const currentPod =
    (detail.pods || []).find((pod) => pod.id === selectedPodId) || detail.pods?.[0];
  const currentDriverDetail = (currentPod?.driverDetails || []).find(
    (entry) => entry.nodeId === selectedDriverId,
  );

  return (
    <div className="rapid-tab-panel" data-testid="rapid-pods-tab">
      <div className="rapid-pod-tabs">
        {(detail.pods || []).map((pod) => (
          <button
            key={pod.id}
            className={`btn ${selectedPodId === pod.id ? 'primary' : 'ghost'} sm`}
            data-testid={`pod-card-${pod.id}`}
            role="tab"
            type="button"
            onClick={() => {
              setSelectedPodId(pod.id);
              setSelectedDriverId(pod.driverNodes?.[0]?.id || null);
            }}
          >
            {pod.name}
          </button>
        ))}
      </div>
      {currentPod ? (
        <section className="rapid-panel">
          <div className="rapid-section-heading">
            <div>
              <div className="eyebrow">{currentPod.id}</div>
              <h3>{currentPod.name}</h3>
            </div>
            <HealthIndicator health={currentPod.healthOverall} />
          </div>
          <p>{currentPod.problemSlice}</p>
          <div className="rapid-chip-row">
            <span className="chip outline sm">Staffing {currentPod.staffingCount}</span>
            <span className="chip outline sm">{currentPod.staffingStatus}</span>
            <span className="chip outline sm">{currentPod.dataOwnershipStatus}</span>
            {currentPod.seededMock ? (
              <span className="chip outline sm">Transcript-derived</span>
            ) : null}
          </div>
          <h4>{summarizePodHealth(currentPod)}</h4>
          <div className="rapid-grid two-up">
            <section>
              <div className="eyebrow">Roster</div>
              <ul className="rapid-record-list">
                {(currentPod.roster || []).map((member) => (
                  <li key={`${member.name}-${member.role}`}>
                    <div className="row spread">
                      <strong>{member.name}</strong>
                      <span className="chip outline sm">{member.coverage}</span>
                    </div>
                    <div className="muted small">{member.role}</div>
                    <p>{member.contribution}</p>
                  </li>
                ))}
              </ul>
            </section>
            <section>
              <div className="eyebrow">Role coverage</div>
              <ul className="rapid-record-list">
                {(currentPod.roleCoverage || []).map((coverage) => (
                  <li key={coverage.role}>
                    <div className="row spread">
                      <strong>{coverage.role}</strong>
                      <StatusChip status={coverage.status}>{coverage.status}</StatusChip>
                    </div>
                    <p>{coverage.note}</p>
                  </li>
                ))}
              </ul>
            </section>
          </div>
          <div className="rapid-grid two-up">
            <section>
              <div className="eyebrow">Driver tree</div>
              <ul className="rapid-record-list">
                {(currentPod.driverNodes || []).map((node) => (
                  <li key={node.id}>
                    <button
                      className={`btn ${selectedDriverId === node.id ? 'primary' : 'ghost'} sm`}
                      data-testid={`driver-node-${node.id}`}
                      type="button"
                      onClick={() => setSelectedDriverId(node.id)}
                    >
                      {node.driver}
                    </button>
                    <div className="muted small">
                      Tier {node.tier} · {node.metric}
                    </div>
                  </li>
                ))}
              </ul>
            </section>
            <section>
              <div className="eyebrow">Driver detail</div>
              {currentDriverDetail ? (
                <div className="rapid-note">
                  <strong>{selectedDriverId}</strong>
                  <p>{currentDriverDetail.summary}</p>
                  <div className="muted small">
                    Metrics: {(currentDriverDetail.linkedMetricIds || []).join(', ')}
                  </div>
                  <div className="muted small">
                    Actions: {(currentDriverDetail.linkedActionIds || []).join(', ')}
                  </div>
                  <div className="muted small">
                    Evidence: {(currentDriverDetail.linkedEvidence || []).join(', ')}
                  </div>
                  <div className="muted small">
                    Root causes: {(currentDriverDetail.linkedRootCauseIds || []).join(', ')}
                  </div>
                </div>
              ) : (
                <div className="rapid-note">Select a driver node to inspect linked context.</div>
              )}
            </section>
          </div>
        </section>
      ) : null}
    </div>
  );
}

export function ActionsTab({ detail, detailState }) {
  // External UAT FINDING-020/021 (+ codex-audit hardening): the detail payload
  // carries only a work-item preview. The board discloses the authoritative
  // total, offers View All via the complete-collection endpoint, and pushes
  // Status/Pod filters to the server so results and totals always describe the
  // complete collection — a filter set from preview mode auto-expands, and the
  // preview cannot be re-entered while a filter is active.
  const [statusFilter, setStatusFilter] = useState('All');
  const [podFilter, setPodFilter] = useState('All');
  const [showAllActions, setShowAllActions] = useState(false);
  const [actionsOffset, setActionsOffset] = useState(0);
  const rapidId = detail.rapid?.id;
  const hasFilters = statusFilter !== 'All' || podFilter !== 'All';
  const expanded = showAllActions || hasFilters;
  const actionsPage = useApi(`/rapids/${rapidId}/work-items`, {
    query: {
      limit: 25,
      offset: actionsOffset,
      ...(statusFilter !== 'All' ? { status: statusFilter } : {}),
      ...(podFilter !== 'All' ? { podId: podFilter } : {}),
    },
    skip: !expanded,
  });
  const previewMeta = detail.previews?.workItems || null;

  // Session-local actions (QA conversions, closeout follow-ons) are not part of
  // the server collection. They obey the same filters and render exactly once —
  // on the first page — so paged ranges never exceed the authoritative total.
  const localActions = (detailState.createdActions || []).filter((action) => {
    if (!action?.id) return false;
    if (statusFilter !== 'All' && action.status !== statusFilter) return false;
    if (podFilter !== 'All' && action.podId !== podFilter) return false;
    return true;
  });
  const serverRows = expanded ? actionsPage.data?.data || [] : detail.workItems || [];
  const includeLocals = !expanded || actionsOffset === 0;
  const actions = buildDerivedActions(
    { workItems: serverRows },
    { createdActions: includeLocals ? localActions : [] },
  );
  const localOnlyCount = includeLocals
    ? actions.length - serverRows.filter((row) => row?.id).length
    : 0;

  const serverTotal = expanded
    ? Number(actionsPage.data?.page?.total ?? serverRows.length)
    : Number(previewMeta?.total ?? serverRows.length);
  const localSuffix = localOnlyCount > 0 ? ` (+${localOnlyCount} added this session)` : '';
  const resultCount = expanded
    ? `Showing ${serverRows.length ? actionsOffset + 1 : 0}–${actionsOffset + serverRows.length} of ${serverTotal} actions${localSuffix}`
    : `Showing ${serverRows.length} of ${serverTotal} actions${localSuffix}`;

  return (
    <div className="rapid-tab-panel" data-testid="rapid-actions-tab">
      <section className="rapid-panel">
        <div className="rapid-section-heading">
          <div>
            <div className="eyebrow">Execution board</div>
            <h3>Execution board</h3>
          </div>
        </div>
        <div className="rapid-toolbar">
          <label>
            Status
            <select
              data-testid="filter-actions-status"
              value={statusFilter}
              onChange={(event) => {
                setStatusFilter(event.target.value);
                setActionsOffset(0);
              }}
            >
              <option value="All">All</option>
              <option value="blocked">Blocked</option>
              <option value="in_progress">In progress</option>
              <option value="todo">To do</option>
              <option value="done">Done</option>
              <option value="waiting">Waiting</option>
            </select>
          </label>
          <label>
            Pod
            <select
              data-testid="filter-actions-pod"
              value={podFilter}
              onChange={(event) => {
                setPodFilter(event.target.value);
                setActionsOffset(0);
              }}
            >
              <option value="All">All</option>
              <option value="POD-1">POD-1</option>
              <option value="POD-2">POD-2</option>
              <option value="POD-3">POD-3</option>
            </select>
          </label>
          <button
            className="btn ghost sm"
            data-testid="filter-clear"
            type="button"
            onClick={() => {
              setStatusFilter('All');
              setPodFilter('All');
              setActionsOffset(0);
            }}
          >
            Clear filters
          </button>
        </div>
        <div className="rapid-chip-row">
          <span className="chip outline sm" data-testid="actions-board-result-count">
            {resultCount}
          </span>
          {!hasFilters && (previewMeta?.truncated || showAllActions) ? (
            <button
              type="button"
              className="btn ghost sm"
              onClick={() => {
                setActionsOffset(0);
                setShowAllActions((value) => !value);
              }}
            >
              {showAllActions ? 'Show actions preview' : `View All ${serverTotal} actions`}
            </button>
          ) : null}
        </div>
        {expanded && actionsPage.loading && !actionsPage.data ? (
          <LoadingSkeleton rows={5} />
        ) : expanded && actionsPage.error ? (
          <ErrorState error={actionsPage.error} onRetry={actionsPage.refresh} />
        ) : actions.length ? (
          <>
            <RapidActionsBoard actions={actions} rapid={detail.rapid} />
            <RapidPoamTable actions={actions} rapid={detail.rapid} />
          </>
        ) : (
          <div className="rapid-empty-inline" data-testid="empty-actions-filtered">
            No actions match the current filters.
          </div>
        )}
        {expanded && !actionsPage.error && actionsPage.data?.page ? (
          <PageViewerControls
            page={actionsPage.data.page}
            pageSize={25}
            pageSizeOptions={[25]}
            label="actions"
            onOffsetChange={setActionsOffset}
          />
        ) : null}
      </section>
    </div>
  );
}

export function RoutedCandidateView({ detail }) {
  const rapid = detail.rapid;
  return (
    <div className="rapid-tab-panel">
      <section className="rapid-panel">
        <div className="eyebrow">Routed / no-go proof</div>
        <h3>{rapid.title}</h3>
        <p>{rapid.summary}</p>
        <div className="rapid-chip-row">
          <span className="chip outline sm">{rapid.id}</span>
          <span className="chip outline sm">{rapid.criteriaScore.total.toFixed(1)}</span>
          <span className="chip outline sm">{rapid.routedDecisionId}</span>
        </div>
        <p>{rapid.routedNote}</p>
      </section>
      {detail.sourceRequest ? (
        <section className="rapid-panel" data-testid="rapid-routed-intake-crosslink">
          <div className="eyebrow">Intake queue linkage</div>
          <h3>Routed to the intake queue</h3>
          <p>
            This candidate was routed out of RAPID. Its intake-queue record carries the same routing
            decision, so the trail is auditable from both sides (AC-RAPID-7).
          </p>
          <div className="rapid-chip-row">
            <span className="chip outline sm">{detail.sourceRequest.workflowState}</span>
            <span className="chip outline sm">
              {detail.sourceRequest.routingOutcome || 'Routing outcome pending'}
            </span>
          </div>
          <Link
            className="btn ghost sm"
            data-testid="rapid-routed-intake-link"
            to={`/project-factory/requests/${detail.sourceRequest.id}`}
          >
            View intake record {detail.sourceRequest.id}
          </Link>
        </section>
      ) : null}
      <CriteriaTab rapid={rapid} />
      <section className="rapid-panel">
        <div className="eyebrow">Decision</div>
        <ul className="rapid-record-list">
          {(rapid.decisions || []).map((decision) => (
            <li key={decision.id}>
              <strong>
                {decision.id} · {decision.decision}
              </strong>
              <p>{decision.rationale}</p>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
